import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ToastController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Appointment } from 'src/app/core/models/appointment.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { SyncService } from 'src/app/core/sync/sync.service';

@Component({
	selector: 'app-appointment-layout',
	templateUrl: './appointment-layout.component.html',
	styleUrls: ['./appointment-layout.component.scss'],
})
export class AppointmentLayoutComponent {

	pageTitle: string;
	appointment: Appointment;
	destroyed$: Subject<boolean>;
	childPageRef: any;

	constructor(
		private syncService: SyncService,
		private activatedRoute: ActivatedRoute,
		private appointmentService: AppointmentService,
		private storageService: LocalStorageService,
		private toastController: ToastController,
		private alertController: AlertController,
		private router: Router,
	) { }

	get isSyncing() {
		return this.syncService.isSyncing$;
	}

	get isBlockingSync$() {
		return this.syncService.isBlockingSync$;
	}

	async ionViewWillEnter(): Promise<void> {
		this.destroyed$ = new Subject();
		await this.loadAppointmentFromRoute();
		this.updateAfterSync();
		this.updateAfterAppointmentChanges();
	}

	ionViewWillLeave(): void {
		this.appointment = null;
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	private updateAfterSync() {
		this.syncService.isSyncing$
			.pipe(takeUntil(this.destroyed$))
			.subscribe(async isSyncing => {
				if (isSyncing === false) { // isSyncing initially set to null. Wait until false
					const currentTicketStatusId = this.appointment.ticketStatusId;
					await this.loadAppointmentFromRoute();
					this.checkForAppointmentChanges(currentTicketStatusId);
				}
			});
	}

	private checkForAppointmentChanges(oldTicketStatusId: number) {
		// Appointment was removed
		if (!this.appointment) {
			this.alertAppointmentRemoved();
			this.router.navigate(['appointments']);
			return;
		}

		// Appointment status has changed
		if (this.appointment && this.appointment.ticketStatusId !== oldTicketStatusId) {
			this.toastChangeStatus(this.appointment.ticketStatusDesc);
		}
	}

	private updateAfterAppointmentChanges() {
		this.appointmentService.activeAppointment$
			.pipe(takeUntil(this.destroyed$))
			.subscribe(newAppointment => {
				if (newAppointment) {
					this.appointment = newAppointment;
				}
			});
	}

	async loadAppointmentFromRoute() {
		const id = this.activatedRoute.snapshot.params.id;
		const appointment = await this.storageService.appointments.getItem<Appointment>(id);

		this.appointment = appointment;
		this.appointmentService.setActiveAppointment(appointment);
		this.setAppointmentOnChildPage();
	}

	async alertAppointmentRemoved(): Promise<void> {
		const statusChangeAlert = await this.alertController.create({
			header: 'Appointment Removed',
			message: 'The appointment was removed during the latest sync',
			buttons: ['OK'],
		});

		await statusChangeAlert.present();
	}

	async toastChangeStatus(status: string): Promise<void> {
		const statusChangeToast = await this.toastController.create({
			message: `The appointment status was updated to ${status}.`,
			duration: 5000,
			position: 'top',
			buttons: ['Dismiss'],
		});

		await statusChangeToast.present();
	}

	onRouteActivate(pageComponentReference: any) {
		this.pageTitle = pageComponentReference.pageTitle;

		this.childPageRef = pageComponentReference;
		this.setAppointmentOnChildPage();
	}

	setAppointmentOnChildPage() {
		if (this.childPageRef && this.childPageRef.setAppointment) {
			this.childPageRef.setAppointment(this.appointment);
		}
	}

}
